// tslint:disable:variable-name

export class CToken {
    protected constructor(public key: string) { }

    public static withKey(key: string): CToken {
        let token = this[key] as CToken;
        if (!token) throw new Error(`Could not find token for key '${key}'`);
        return token;
    }

    public static hasKey(key: string): boolean {
        return this[key] != null;
    }

    // configurator models

    public static Accordion = new CToken("Accordion");
    public static ActionBar = new CToken("ActionBar");
    public static Build = new CToken("Build");
    public static Button = new CToken("Button");
    public static Configurator = new CToken("Configurator");
    public static ConfiguratorAction = new CToken("ConfiguratorAction");
    public static ConfiguratorFunction = new CToken("ConfiguratorFunction");
    public static Container = new CToken("Container");
    public static DropdownButton = new CToken("DropdownButton");
    public static Expander = new CToken("Expander");
    public static Export = new CToken("Export");
    public static Field = new CToken("Field");
    public static GridColumn = new CToken("GridColumn");
    public static GridRow = new CToken("GridRow");
    public static Group = new CToken("Group");
    public static Html = new CToken("Html");
    public static SvgViewer = new CToken("SvgViewer");
    public static Hotspot = new CToken("Hotspot");
    public static KbObject = new CToken("KbObject");
    public static LayoutConfig = new CToken("LayoutConfig");
    public static LayoutContent = new CToken("LayoutContent");
    public static Media = new CToken("Media");
    public static NestedElement = new CToken("NestedElement");
    public static NestedSet = new CToken("NestedSet");
    public static OptionFilter = new CToken("OptionFilter");
    public static Option = new CToken("Option");
    public static OverlayPopup = new CToken("OverlayPopup");
    public static OverlayPopupControl = new CToken("OverlayPopupControl");
    public static Page = new CToken("Page");
    public static PageControl = new CToken("PageControl");
    public static PageNavigator = new CToken("PageNavigator");
    public static PageTabs = new CToken("PageTabs");
    public static Popup = new CToken("Popup");
    public static PriceWidget = new CToken("PriceWidget");
    public static PricingRuleContainer = new CToken("PricingRuleContainer");
    public static ProductRuleContainer = new CToken("ProductRuleContainer");
    public static ReferencedConfigurator = new CToken("ReferencedConfigurator");
    public static RenderPassExport = new CToken("RenderPassExport");
    public static RuleContainer = new CToken("RuleContainer");
    public static Rule = new CToken("Rule");
    public static Tab = new CToken("Tab");
    public static TabControl = new CToken("TabControl");
    public static Text = new CToken("Text");
    public static Toolbar = new CToken("Toolbar");
    public static Viewer = new CToken("Viewer");

    // outputs
    public static BomOutput = new CToken("BomOutput");
    public static CadDrawingOutput = new CToken("CadDrawingOutput");
    public static CadOutput = new CToken("CadOutput");
    public static ExcelOutput = new CToken("ExcelOutput");
    public static FileOutput = new CToken("FileOutput");
    public static ImageOutput = new CToken("ImageOutput");
    public static Output = new CToken("Output");
    public static PdfOutput = new CToken("PdfOutput");
    public static SceneRenderOutput = new CToken("SceneRenderOutput");
    public static WordOutput = new CToken("WordOutput");
    public static ZipOutput = new CToken("ZipOutput");
    public static SvgOutput = new CToken("SvgOutput");
    public static TextOutput = new CToken("TextOutput");
    public static GlbOutput = new CToken("GlbOutput");
    public static FbxOutput = new CToken("FbxOutput");

    // output fields
    public static ExcelCellField = new CToken("ExcelCellField");
    public static ExcelChartField = new CToken("ExcelChartField");
    public static ExcelImageField = new CToken("ExcelImageField");
    public static ExcelSheetField = new CToken("ExcelSheetField");
    public static ExcelTableField = new CToken("ExcelTableField");
    public static OutputField = new CToken("OutputField");
    public static PdfSectionField = new CToken("PdfSectionField");
    public static WordContainerField = new CToken("WordContainerField");
    public static WordImageField = new CToken("WordImageField");
    public static WordListField = new CToken("WordListField");
    public static WordTextField = new CToken("WordTextField");
    public static ZipSectionField = new CToken("ZipSectionField");

    // cad output fields
    public static CadAnnotationField = new CToken("CadAnnotationField");
    public static CadBlockArrayField = new CToken("CadBlockArrayField");
    public static CadBlockField = new CToken("CadBlockField");
    public static CadComponentField = new CToken("CadComponentField");
    public static CadCustomPropertyField = new CToken("CadCustomPropertyField");
    public static CadDimensionField = new CToken("CadDimensionField");
    public static CadDrawingAnnotationField = new CToken("CadDrawingAnnotationField");
    public static CadDrawingDimensionField = new CToken("CadDrawingDimensionField");
    public static CadDrawingSheetField = new CToken("CadDrawingSheetField");
    public static CadDrawingViewField = new CToken("CadDrawingViewField");
    public static CadDynamicComponentField = new CToken("CadDynamicComponentField");
    public static CadDynamicDrawingAnnotationField = new CToken("CadDynamicDrawingAnnotationField");
    public static CadDynamicDrawingDimensionField = new CToken("CadDynamicDrawingDimensionField");
    public static CadFeatureField = new CToken("CadFeatureField");
    public static CadLayerField = new CToken("CadLayerField");
    public static CadLibFeatureField = new CToken("CadLibFeatureField");
    public static CadParameterField = new CToken("CadParameterField");
    public static CadReplaceComponentField = new CToken("CadReplaceComponentField");
    public static CadRepresentationField = new CToken("CadRepresentationField");
    public static CadScaleFeatureField = new CToken("CadScaleFeatureField");
    public static CadTextField = new CToken("CadTextField");

    // mates
    public static CadAngleMate = new CToken("CadAngleMate");
    public static CadCoincidentMate = new CToken("CadCoincidentMate");
    public static CadCoordinateSystemMate = new CToken("CadCoordinateSystemMate");
    public static CadDistanceMate = new CToken("CadDistanceMate");
    public static CadLibDimension = new CToken("CadLibDimension");
    public static CadLibReference = new CToken("CadLibReference");
    public static CadPositionConstraint = new CToken("CadPositionConstraint");
    public static Constraint = new CToken("Constraint");
    public static Mate = new CToken("Mate");

    // manufacture
    public static PartRev = new CToken("PartRev");
    public static PartMaterial = new CToken("PartMaterial");
    public static PartOperation = new CToken("PartOperation");
    public static PartOperationDetail = new CToken("PartOperationDetail");
    public static MethodOfManufacture = new CToken("MethodOfManufacture");
    public static BistrackMethodOfManufacture = new CToken("BistrackMethodOfManufacture");
    public static BistrackPartRev = new CToken("BistrackPartRev");
}
